import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

const emptyForm = () => ({
  name: "",
  channel_tags: "zomato, swiggy",
  packaging_default: "",
  sla_minutes: 20,
  color: "#E85D04",
  active: true,
});

export default function Brands() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [range, setRange] = useState(defaultRange);

  const { data: brands = [] } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => (await api.get("/brands")).data,
  });

  const { data: pnl } = useQuery({
    queryKey: ["brands-pnl", range.from, range.to],
    queryFn: async () => (await api.get(`/brands/pnl?from=${range.from}&to=${range.to}`)).data,
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "",
      channel_tags: Array.isArray(b.channel_tags) ? b.channel_tags.join(", ") : "",
      packaging_default: b.packaging_default || "",
      sla_minutes: b.sla_minutes || 20,
      color: b.color || "#E85D04",
      active: b.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      name: form.name.trim(),
      channel_tags: form.channel_tags.split(",").map((s) => s.trim()).filter(Boolean),
      packaging_default: form.packaging_default,
      sla_minutes: Number(form.sla_minutes) || 20,
      color: form.color,
      active: form.active,
    };
    try {
      if (editing) {
        await api.put(`/brands/${editing.id}`, payload);
        toast.success("Brand updated");
      } else {
        await api.post("/brands", payload);
        toast.success("Brand created");
      }
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["brands-pnl"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brand “${b.name}”?`)) return;
    try {
      await api.delete(`/brands/${b.id}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["brands-pnl"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="brands-page">
      <PageHeader title="Cloud Kitchen Brands" subtitle="Virtual brands, packaging defaults, and P&L">
        <Button onClick={openCreate} className="bg-[var(--kul-primary)] text-white">
          <Plus className="w-4 h-4 mr-1" /> Add brand
        </Button>
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mb-6">
        {brands.map((b) => (
          <div key={b.id} className="kul-card p-4 space-y-2" data-testid={`brand-${b.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${b.color || "#E85D04"}22` }}>
                  <Tag className="w-4 h-4" style={{ color: b.color || "#E85D04" }} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{b.name}</div>
                  <div className="text-xs text-neutral-400">SLA {b.sla_minutes || 20}m</div>
                </div>
              </div>
              <Badge className={b.active !== false ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"}>
                {b.active !== false ? "Active" : "Off"}
              </Badge>
            </div>
            {b.packaging_default && <p className="text-xs text-neutral-500">Pack: {b.packaging_default}</p>}
            <div className="flex flex-wrap gap-1">
              {(b.channel_tags || []).map((t) => (
                <Badge key={t} className="bg-neutral-100 text-neutral-600 text-[10px] capitalize">{t}</Badge>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => openEdit(b)}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(b)} className="text-[var(--kul-danger)]"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
        {!brands.length && <p className="text-neutral-400 col-span-full">No brands yet.</p>}
      </div>

      <div className="kul-card p-5 max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h3 className="font-display font-700">Brand P&L</h3>
          <div className="flex gap-2">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9" />
            </div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-neutral-400 border-b">
              <th className="py-2 font-medium">Brand</th>
              <th className="py-2 font-medium">Orders</th>
              <th className="py-2 font-medium">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {(pnl?.brands || []).map((row) => (
              <tr key={row.brand_id || "none"} className="border-b last:border-0">
                <td className="py-2 font-medium">{row.brand_name}</td>
                <td className="py-2">{row.orders}</td>
                <td className="py-2 font-mono-k">{money(row.revenue)}</td>
              </tr>
            ))}
            {!pnl?.brands?.length && (
              <tr><td colSpan={3} className="py-4 text-neutral-400">No paid orders in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit brand" : "Add brand"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Channel tags</Label><Input value={form.channel_tags} onChange={(e) => setForm({ ...form, channel_tags: e.target.value })} placeholder="zomato, swiggy" /></div>
            <div><Label>Default packaging</Label><Input value={form.packaging_default} onChange={(e) => setForm({ ...form, packaging_default: e.target.value })} placeholder="Eco box + napkin" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>SLA minutes</Label><Input type="number" value={form.sla_minutes} onChange={(e) => setForm({ ...form, sla_minutes: e.target.value })} /></div>
              <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 p-1" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[var(--kul-primary)] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
